import usePermissions from "../../hooks/usePermissions";
import { Result, Button } from "antd";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { resolveHomePath } from "../lib/homePath";

/**
 * Wraps children and only renders them if the user has the required permission.
 *
 * Props:
 * - permission  (string)   single required permission
 * - any         (string[]) render if user has ANY of these permissions
 * - fallback    (node)     optional custom fallback (defaults to 403 page)
 * - hide        (bool)     if true, renders nothing instead of a 403 (useful for buttons)
 * - redirectTo  (string)   if set and denied, Navigate here instead of showing 403
 */
const PermissionGuard = ({ permission, any, fallback, hide = false, redirectTo, children }) => {
  const { hasPermission, hasAny, permissions, isAdmin } = usePermissions();
  const { t } = useTranslation();

  let allowed = false;
  if (permission) {
    allowed = hasPermission(permission);
  } else if (any && any.length > 0) {
    allowed = hasAny(any);
  } else {
    allowed = true;
  }

  if (allowed) return children;

  if (hide) return null;

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (fallback) return fallback;

  const home = resolveHomePath(permissions, isAdmin);

  return (
    <Result
      status="403"
      title="403"
      subTitle={t("noPermission")}
      extra={
        <Link to={home}>
          <Button type="primary">{t("backToDashboard")}</Button>
        </Link>
      }
    />
  );
};

export default PermissionGuard;
